import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { cn } from './cn';
import { FormField, type FormFieldProps } from './FormField';

export interface FormTextFieldProps extends Omit<FormFieldProps, 'children'> {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  type?: string;
  /** When true and type is "password", renders an eye icon button to toggle visibility. */
  revealable?: boolean;
  multiline?: boolean;
  rows?: number;
  prefix?: string;
  suffix?: string;
  autoComplete?: string;
  name?: string;
  inputClassName?: string;
}

export const FormTextField = React.forwardRef<HTMLInputElement, FormTextFieldProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      onBlur,
      onFocus,
      placeholder,
      disabled,
      readOnly,
      type = 'text',
      revealable,
      multiline,
      rows = 3,
      prefix,
      suffix,
      autoComplete,
      name,
      inputClassName,
      label,
      description,
      errorMessage,
      successMessage,
      required,
      className,
      id,
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const [revealed, setRevealed] = React.useState(false);

    const inputClasses = cn(
      'w-full rounded-md border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-gray-400 transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500',
      errorMessage ? 'border-danger-400 focus:ring-danger-500 focus:border-danger-500' : 'border-gray-300',
      disabled && 'bg-gray-50 text-gray-500 cursor-not-allowed',
      readOnly && 'bg-gray-50',
      revealable && 'pr-9',
      inputClassName
    );

    const inputProps = {
      id: inputId,
      value,
      defaultValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange?.(e.target.value),
      onBlur,
      onFocus,
      placeholder,
      disabled,
      readOnly,
      autoComplete,
      name,
    };

    return (
      <FormField
        label={label}
        description={description}
        errorMessage={errorMessage}
        successMessage={successMessage}
        required={required}
        className={className}
        id={inputId}
      >
        <div className="flex items-center">
          {prefix && <span className="mr-2 text-sm text-gray-500 whitespace-nowrap">{prefix}</span>}
          {multiline ? (
            <textarea className={inputClasses} rows={rows} {...inputProps} />
          ) : revealable ? (
            <div className="relative flex-1">
              <input ref={ref} type={revealed ? 'text' : 'password'} className={inputClasses} {...inputProps} />
              <button
                type="button"
                tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer bg-transparent border-none p-0"
                onClick={() => setRevealed((v) => !v)}
                aria-label={revealed ? 'Hide' : 'Reveal'}
              >
                {revealed ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </button>
            </div>
          ) : (
            <input ref={ref} type={type} className={inputClasses} {...inputProps} />
          )}
          {suffix && <span className="ml-2 text-sm text-gray-500 whitespace-nowrap">{suffix}</span>}
        </div>
      </FormField>
    );
  }
);
FormTextField.displayName = 'FormTextField';
